// Carruseles horizontales: [data-nat-scroller] con un [data-nat-track] que scrollea y botones
// [data-nat-prev]/[data-nat-next]. Cada flecha avanza dos ítems y se desactiva en los extremos.
(() => {
  function init(root) {
    if (root.dataset.natScrollerReady) return;
    root.dataset.natScrollerReady = '1';
    const track = root.querySelector('[data-nat-track]');
    const prev = root.querySelector('[data-nat-prev]');
    const next = root.querySelector('[data-nat-next]');
    if (!track || !prev || !next) return;
    const step = () => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item ? (item.getBoundingClientRect().width + gap) * 2 : track.clientWidth;
    };
    const loop = autoplay(root, track);
    const update = () => {
      if (loop) { prev.parentElement.hidden = loop.half() <= track.clientWidth; return; }
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      // Si entra todo, las flechas no tienen nada que hacer: se ocultan.
      prev.parentElement.hidden = prev.disabled && next.disabled;
    };
    prev.addEventListener('click', () => { loop?.wrap(-step()); track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    next.addEventListener('click', () => { loop?.wrap(step()); track.scrollBy({ left: step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // [data-nat-autoplay="<px por segundo>"]: desliza de forma continua y sin fin (los ítems se
  // duplican y al pasar la primera vuelta se salta hacia atrás sin que se note). Se frena con el
  // mouse o el foco encima, mientras alguien lo toca, fuera de pantalla o con movimiento reducido.
  function autoplay(root, track) {
    const speed = parseFloat(root.dataset.natAutoplay);
    if (!speed || matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const items = [...track.children];
    for (const item of items) {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      track.append(clone);
    }
    // Sin snap ni smooth: si no, cada avance de un píxel se anima o vuelve al ítem más cercano.
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    // Largo de una vuelta: de dónde empieza el primer ítem a dónde empieza su copia (incluye el gap).
    const half = () => track.children[items.length].offsetLeft - items[0].offsetLeft;
    // Antes de que una flecha cruce un borde, se salta a la copia equivalente.
    const wrap = (delta) => {
      if (delta > 0 && track.scrollLeft + delta >= half()) track.scrollLeft -= half();
      if (delta < 0 && track.scrollLeft + delta < 0) track.scrollLeft += half();
    };
    let hover = false, focus = false, touch = false, visible = false, pos = track.scrollLeft, last = 0;
    const frame = (t) => {
      const dt = last ? Math.min(t - last, 100) : 0;
      last = t;
      if (!(hover || focus || touch || !visible || document.hidden)) {
        // Si alguien movió el carrusel a mano (flechas, swipe), se sigue desde ahí.
        if (Math.abs(track.scrollLeft - pos) > 2) pos = track.scrollLeft;
        pos += (speed * dt) / 1000;
        if (pos >= half()) pos -= half();
        track.scrollLeft = pos;
      }
      requestAnimationFrame(frame);
    };
    root.addEventListener('mouseenter', () => { hover = true; });
    root.addEventListener('mouseleave', () => { hover = false; });
    root.addEventListener('focusin', () => { focus = true; });
    root.addEventListener('focusout', () => { focus = false; });
    track.addEventListener('touchstart', () => { touch = true; }, { passive: true });
    track.addEventListener('touchend', () => { setTimeout(() => { touch = false; }, 2000); }, { passive: true });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(root);
    requestAnimationFrame(frame);
    return { wrap, half };
  }
  const initAll = () => document.querySelectorAll('[data-nat-scroller]').forEach(init);
  initAll();
  // En el editor de temas las secciones se re-renderizan sin recargar la página.
  document.addEventListener('shopify:section:load', initAll);
})();

// Paneles laterales genéricos: [data-nat-sheet-open="<id del dialog>"] abre, [data-nat-sheet-close]
// o el fondo cierran, Esc cierra con animación. Mientras está abierto, Horizon no scrollea la página.
(() => {
  let lastFocus = null;

  function close(sheet) {
    if (!sheet?.open) return;
    sheet.classList.add('is-closing');
    const done = () => { sheet.classList.remove('is-closing'); sheet.close(); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else sheet.addEventListener('animationend', done, { once: true });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-nat-sheet-open]');
    if (opener) {
      const sheet = document.getElementById(opener.dataset.natSheetOpen);
      if (!sheet) return;
      lastFocus = opener;
      sheet.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }
    const sheet = event.target.closest('.nat-sheet');
    if (!sheet) return;
    if (event.target === sheet || event.target.closest('[data-nat-sheet-close]')) close(sheet);
  });

  // Un formulario dentro de un panel (p. ej. el newsletter) recarga la página al enviarse: si
  // volvió con el resultado o con un error, el panel se reabre para mostrarlo.
  const reabrir = () => {
    const sheet = document.querySelector('.nat-sheet:has([data-nat-newsletter-ok], [aria-invalid="true"])');
    if (sheet && !sheet.open) {
      sheet.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reabrir);
  else reabrir();

  // Botones [data-nat-copy="texto"]: copian al portapapeles y avisan en el propio botón.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-nat-copy]');
    if (!button) return;
    const label = button.querySelector('[data-nat-copy-label]') || button;
    const original = label.textContent;
    try {
      await navigator.clipboard.writeText(button.dataset.natCopy);
      label.textContent = 'Copiado ✓';
    } catch {
      // Sin permiso de portapapeles (http, navegador viejo): se selecciona el código para copiarlo a mano.
      const code = button.parentElement.querySelector('.nat-newsletter__code');
      if (code) getSelection().selectAllChildren(code);
      label.textContent = 'Seleccionado: copialo';
    }
    setTimeout(() => (label.textContent = original), 2500);
  });

  // Newsletter por el Worker de suscripción: responde sin recargar si el email es nuevo, si ya
  // estaba suscripto o si ya compró. Si el proxy no contesta, se envía el form nativo.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-nat-newsletter]');
    if (!form || form.dataset.natNativo) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const boton = form.querySelector('button[type="submit"]');
    const consent = (k) => form.querySelector(`[data-nat-consent="${k}"]`)?.checked === true;
    boton.disabled = true;
    let datos;
    try {
      const r = await fetch((window.Shopify?.routes?.root || '/') + 'apps/naturalie/suscribir', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ email: form.querySelector('input[type="email"]').value, marketing: consent('marketing'), privacidad: consent('privacidad') }),
      });
      if (!(r.headers.get('content-type') || '').includes('json')) throw new Error('sin proxy');
      datos = await r.json();
    } catch {
      // requestSubmit (no submit): dispara el hCaptcha de Shopify y el cierre del panel.
      form.dataset.natNativo = '1';
      form.requestSubmit();
      return;
    } finally {
      boton.disabled = false;
    }
    if (datos.error) {
      let err = form.querySelector('[data-nat-newsletter-error]');
      if (!err) {
        err = document.createElement('p');
        err.className = 'nat-newsletter__error';
        err.setAttribute('role', 'alert');
        err.dataset.natNewsletterError = '';
        form.querySelector('.nat-newsletter__consents').after(err);
      }
      err.textContent = datos.error;
      return;
    }
    const plantilla = form.querySelector('template[data-nat-newsletter-resultado]').content.cloneNode(true);
    plantilla.querySelector('[data-mensaje]').textContent = datos.mensaje;
    if (datos.codigo) {
      plantilla.querySelector('[data-codigo]').textContent = datos.codigo;
      plantilla.querySelector('[data-nat-copy]').dataset.natCopy = datos.codigo;
    } else {
      plantilla.querySelector('[data-con-codigo]').remove();
    }
    form.replaceChildren(plantilla);
  }, true); // captura: corre antes que el listener de hCaptcha que Shopify pone en el form

  // Al enviar un formulario desde un panel, el panel se cierra: si Shopify muestra su captcha
  // (hCaptcha), un <dialog> modal lo taparía y no se podría completar. La página vuelve con el
  // resultado y `reabrir` muestra el panel otra vez.
  document.addEventListener('submit', (event) => {
    const sheet = event.target.closest?.('.nat-sheet');
    if (sheet?.open) {
      sheet.close();
      document.documentElement.removeAttribute('scroll-lock');
    }
  });

  document.addEventListener('cancel', (event) => {
    if (event.target.matches?.('.nat-sheet')) { event.preventDefault(); close(event.target); }
  }, true);

  document.addEventListener('close', (event) => {
    if (!event.target.matches?.('.nat-sheet')) return;
    document.documentElement.removeAttribute('scroll-lock');
    // Un link de ancla dentro del panel (p. ej. #NatDudas) lleva el foco a su destino, no al botón.
    if (!location.hash) lastFocus?.focus?.();
  }, true);
})();
